import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import JsBarcode from "jsbarcode";
import { QRCodeCanvas } from "qrcode.react";
import jsPDF from "jspdf";
import { Printer, Search, X, Grid3x3, Plus, Minus } from "lucide-react";

const COLS = 4;
const ROWS = 11;
const TOTAL = COLS * ROWS; // 44 - foglio A4 edge-to-edge (52.5 × 27 mm per etichetta)
const LABEL_W = 52.5;
const LABEL_H = 27;
// Safe area interna: nessun contenuto vicino al bordo (evita sconfinamento in caso di lieve disallineamento in stampa)
const SAFE_M = 2; // margine di sicurezza interno di ogni etichetta (in mm)

export default function LabelsPage() {
  const [sp] = useSearchParams();
  const preId = sp.get("id");
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [selected, setSelected] = useState([]); // [{...product, copies}]
  const [startPos, setStartPos] = useState(1); // 1..44

  useEffect(() => {
    if (preId) (async () => {
      const { data } = await api.get(`/products/${preId}`);
      setSelected([{ ...data, copies: 1 }]);
    })();
  }, [preId]);

  const search = async (val) => {
    if (val.length < 1) { setResults([]); return; }
    const { data } = await api.get("/products", { params: { q: val } });
    setResults(data.slice(0, 8));
  };
  const addProduct = (p) => {
    if (!selected.find(s => s.id === p.id)) setSelected([...selected, { ...p, copies: 1 }]);
    setQ(""); setResults([]);
  };
  const removeProduct = (id) => setSelected(selected.filter(p => p.id !== id));
  const setCopies = (id, n) => setSelected(selected.map(p => p.id === id ? { ...p, copies: Math.max(1, Math.min(99, n)) } : p));

  const totalLabels = selected.reduce((s, p) => s + (p.copies || 1), 0);
  const labelsAvailable = TOTAL - (startPos - 1);
  const fitsOnePage = totalLabels <= labelsAvailable;
  const sheetsNeeded = Math.ceil((totalLabels - labelsAvailable) / TOTAL) + 1;

  const printPDF = async () => {
    // Expand copies into a flat list
    const flat = [];
    selected.forEach(p => { for (let i = 0; i < (p.copies || 1); i++) flat.push(p); });
    if (flat.length === 0) return;

    const doc = new jsPDF({ unit: "mm", format: "a4", orientation: "portrait" });

    let cell = startPos - 1; // 0-indexed
    let pageIdx = 0;

    for (let i = 0; i < flat.length; i++) {
      if (cell >= TOTAL) {
        doc.addPage();
        cell = 0;
        pageIdx++;
      }
      const row = Math.floor(cell / COLS);
      const col = cell % COLS;
      const x = col * LABEL_W;
      const y = row * LABEL_H;
      drawLabel(doc, flat[i], x, y);
      cell++;
    }
    doc.save(`Etichette_GERMAC_${new Date().toISOString().slice(0,10)}.pdf`);
  };

  return (
    <div className="p-4 md:p-8 max-w-7xl mx-auto">
      <div className="mb-6">
        <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2">Stampa</div>
        <h1 className="font-display text-4xl font-black uppercase tracking-tight">Etichette Prodotti</h1>
        <p className="text-zinc-400 text-sm mt-1">Formato 52.5 × 27 mm — foglio A4 da 44 etichette (4 colonne × 11 righe). Compatibile etichettatrici standard.</p>
      </div>

      {/* Aggiungi prodotti */}
      <div className="gm-card p-5 mb-5">
        <label className="block text-xs font-bold uppercase tracking-widest text-zinc-400 mb-2">Aggiungi prodotti</label>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" size={18} />
          <input data-testid="labels-search-input" value={q} onChange={(e)=>{setQ(e.target.value); search(e.target.value);}} className="gm-input pl-11" placeholder="Cerca per codice o descrizione" />
        </div>
        {results.length > 0 && (
          <div className="mt-3 border border-zinc-800 divide-y divide-zinc-800/50">
            {results.map(p => (
              <button key={p.id} onClick={()=>addProduct(p)} className="w-full text-left p-3 hover:bg-zinc-900 flex items-center justify-between" data-testid="labels-add-product">
                <div>
                  <div className="font-mono-data text-xs text-[#4CAF50]">{p.codice_interno}</div>
                  <div className="text-sm">{p.descrizione}</div>
                </div>
                <span className="text-xs text-[#4CAF50] uppercase tracking-widest">+ Aggiungi</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {selected.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {/* Lista prodotti + copie */}
          <div className="lg:col-span-1 space-y-3">
            <div className="text-xs uppercase tracking-widest text-zinc-400">Prodotti ({selected.length})</div>
            {selected.map(p => (
              <div key={p.id} className="gm-card p-3 flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="font-mono-data text-xs text-[#4CAF50]">{p.codice_interno}</div>
                  <div className="text-sm truncate">{p.descrizione}</div>
                </div>
                <div className="flex items-center gap-1">
                  <button onClick={()=>setCopies(p.id, (p.copies||1)-1)} className="h-8 w-8 border border-zinc-700 flex items-center justify-center"><Minus size={14}/></button>
                  <input type="number" value={p.copies||1} onChange={e=>setCopies(p.id, parseInt(e.target.value)||1)} className="h-8 w-12 text-center bg-transparent border border-zinc-700 font-mono-data" data-testid="label-copies-input" />
                  <button onClick={()=>setCopies(p.id, (p.copies||1)+1)} className="h-8 w-8 border border-zinc-700 flex items-center justify-center"><Plus size={14}/></button>
                </div>
                <button onClick={()=>removeProduct(p.id)} className="text-[#FF3B30] p-1"><X size={14}/></button>
              </div>
            ))}
          </div>

          {/* Anteprima foglio A4 con selezione posizione */}
          <div className="lg:col-span-2">
            <div className="flex items-end justify-between mb-3 flex-wrap gap-2">
              <div>
                <div className="text-xs uppercase tracking-widest text-zinc-400">Posizione di partenza sul foglio</div>
                <div className="text-sm text-zinc-500">Click su una cella per scegliere da dove iniziare la stampa. Utile per fogli già parzialmente utilizzati.</div>
              </div>
              <div className="flex items-center gap-3">
                <div className="text-right">
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500">Etichette totali</div>
                  <div className="font-display text-2xl font-black text-[#4CAF50]">{totalLabels}</div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] uppercase tracking-widest text-zinc-500">Fogli A4</div>
                  <div className="font-display text-2xl font-black">{sheetsNeeded}</div>
                </div>
              </div>
            </div>

            <div className="gm-card p-3 mb-3">
              <div className="bg-white p-2 rounded" style={{ aspectRatio: "210/297", maxWidth: 400, margin: "0 auto" }}>
                <div className="grid h-full" style={{ gridTemplateColumns: `repeat(${COLS}, 1fr)`, gridTemplateRows: `repeat(${ROWS}, 1fr)`, gap: 1 }}>
                  {Array.from({ length: TOTAL }).map((_, i) => {
                    const pos = i + 1;
                    const isStart = pos === startPos;
                    const usedOnPage1 = pos >= startPos && pos < startPos + totalLabels && pos <= TOTAL;
                    return (
                      <button key={i}
                        onClick={() => setStartPos(pos)}
                        title={`Posizione ${pos}`}
                        data-testid={`label-position-${pos}`}
                        className={`relative border transition-all ${
                          isStart ? "bg-[#4CAF50] border-[#4CAF50] text-white"
                          : usedOnPage1 ? "bg-[#4CAF50]/30 border-[#4CAF50]/50 text-[#4CAF50]"
                          : "bg-zinc-100 border-zinc-300 text-zinc-400 hover:bg-zinc-200"
                        }`}
                        style={{ fontSize: 8, fontWeight: 700 }}
                      >
                        {pos}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className="flex gap-2 justify-center mt-3 text-xs">
                <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 bg-[#4CAF50]" /> Posizione iniziale</span>
                <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 bg-[#4CAF50]/30 border border-[#4CAF50]/50" /> Occupata</span>
                <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 bg-zinc-200 border border-zinc-300" /> Saltata</span>
              </div>
              <div className="flex items-center gap-2 mt-3 justify-center text-sm">
                <Grid3x3 size={14} className="text-zinc-500" />
                <span className="text-zinc-500">Inizia da posizione:</span>
                <input type="number" min="1" max={TOTAL} value={startPos} onChange={e=>setStartPos(Math.max(1, Math.min(TOTAL, parseInt(e.target.value)||1)))} className="bg-transparent border-b border-zinc-700 outline-none w-14 text-center font-mono-data" data-testid="start-pos-input" />
                <span className="text-zinc-500">di {TOTAL}</span>
              </div>
              {!fitsOnePage && (
                <div className="text-xs text-[#FF9F0A] mt-2 text-center">
                  ⚠ Le etichette occuperanno {sheetsNeeded} fogli A4 ({labelsAvailable} sul primo, le rimanenti sui successivi)
                </div>
              )}
            </div>

            <button onClick={printPDF} disabled={totalLabels === 0} className="gm-btn-primary w-full h-12 px-5 rounded-sm flex items-center justify-center gap-2" data-testid="labels-print-button">
              <Printer size={16} /> Genera PDF ({totalLabels} etichett{totalLabels===1?"a":"e"})
            </button>
          </div>
        </div>
      )}

      {selected.length === 0 && (
        <div className="text-zinc-500 text-sm text-center py-12">Aggiungi prodotti dalla ricerca sopra per iniziare</div>
      )}

      {/* Offscreen QR canvases used by jsPDF */}
      <div style={{ position: "absolute", left: -9999, top: -9999 }}>
        {selected.map(p => (
          <div key={p.id} id={`qr-cache-${p.id}`}>
            <QRCodeCanvas value={p.codice_interno} size={64} />
          </div>
        ))}
      </div>
    </div>
  );
}

// Draws a single label inside the PDF at position (x,y) with size LABEL_W × LABEL_H
// Tutto il contenuto è confinato in una safe-area con margine SAFE_M da ogni bordo.
function drawLabel(doc, p, x, y) {
  const W = LABEL_W;
  const H = LABEL_H;
  const M = SAFE_M;              // margine di sicurezza interno
  // Coordinate safe (dentro le quali disegniamo tutto):
  const sx = x + M;              // safe-left
  const sy = y + M;              // safe-top
  const sr = x + W - M;          // safe-right
  const sb = y + H - M;          // safe-bottom
  const sw = W - 2 * M;          // safe width

  // Top row: GERMAC + posizione
  doc.setFont("helvetica", "bold").setFontSize(6).setTextColor(0,0,0);
  doc.text("GERMAC", sx, sy + 2);
  doc.setFont("courier", "bold").setFontSize(6.5);
  doc.text(p.posizione || "—", sr, sy + 2, { align: "right" });

  // Description (max 2 lines) — lasciamo spazio per QR (largo 8 mm + gap 1 mm)
  doc.setFont("helvetica", "bold").setFontSize(6.5);
  const descLines = doc.splitTextToSize(p.descrizione || "", sw - 10);
  doc.text(descLines.slice(0, 2), sx, sy + 5);

  // Codice interno (grande)
  doc.setFont("courier", "bold").setFontSize(8).setTextColor(0,0,0);
  doc.text(p.codice_interno, sx, sy + 12);

  // Codice fornitore (piccolo)
  doc.setFont("helvetica", "normal").setFontSize(5).setTextColor(60,60,60);
  doc.text(`Forn: ${(p.codice_fornitore || "—").substring(0, 20)}`, sx, sy + 15);

  // Barcode (CODE128) — bottom-left, largo max 28 mm (lascia spazio al QR di 8 mm + gap)
  const bcH = 6;                    // altezza barcode
  const bcW = Math.min(28, sw - 10);
  const bcY = sb - bcH - 2;         // 2 mm sopra safe-bottom (spazio per testo codice sotto)
  try {
    const c = document.createElement("canvas");
    JsBarcode(c, p.codice_interno, {
      format: "CODE128", height: 18, width: 1.0,
      displayValue: false, margin: 0, background: "#ffffff",
    });
    doc.addImage(c.toDataURL("image/png"), "PNG", sx, bcY, bcW, bcH);
    doc.setFont("courier", "normal").setFontSize(4.5).setTextColor(0,0,0);
    doc.text(p.codice_interno, sx + bcW / 2, bcY + bcH + 1.6, { align: "center" });
  } catch { /* barcode facoltativo */ }

  // QR code right-bottom, 8×8mm, allineato dentro safe area
  const qrSize = 8;
  const qrX = sr - qrSize;       // allineato al bordo destro safe
  const qrY = sb - qrSize;       // allineato al bordo inferiore safe
  const qrEl = document.getElementById(`qr-cache-${p.id}`);
  if (qrEl) {
    const qrCanvas = qrEl.querySelector("canvas");
    if (qrCanvas) {
      doc.addImage(qrCanvas.toDataURL("image/png"), "PNG", qrX, qrY, qrSize, qrSize);
    }
  }

  // Guida taglio grigia opzionale (utile in fase di taglio manuale — commentare in produzione)
  // doc.setDrawColor(230,230,230).setLineWidth(0.05).rect(x, y, W, H);
}

// Hidden QR cache for PDF generation (mounted in App via portal or here)
export function QrCache({ products }) {
  return (
    <div style={{ position: "absolute", left: -9999, top: -9999 }}>
      {products.map(p => (
        <div key={p.id} id={`qr-cache-${p.id}`}>
          <QRCodeCanvas value={p.codice_interno} size={64} />
        </div>
      ))}
    </div>
  );
}
